import { useState, useCallback, useMemo } from 'react';
import { notifications } from '@mantine/notifications';
import type { CSVColumnMapping, CSVImportPreviewRow, CSVImportPreview, CSVImportResult } from '../../../../types';
import { participantsApi } from '../../../../services/api/participants';
import { STANDARD_FIELDS, REQUIRED_FIELDS } from './importConstants';
import type { ImportStep, FieldType, MappingSummary, PreviewStats, ReviewColumn } from './importTypes';

interface UseCSVImportOptions {
  cohortId: string;
  cohortName: string;
  partnerName?: string;
  existingParticipantCount: number;
  onClose: () => void;
  onImportComplete: () => void;
}

export function useCSVImport({
  cohortId,
  cohortName,
  partnerName,
  existingParticipantCount,
  onClose,
  onImportComplete,
}: UseCSVImportOptions) {
  const [activeStep, setActiveStep] = useState<ImportStep>(0);
  const [file, setFile] = useState<File | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [mappings, setMappings] = useState<CSVColumnMapping[]>([]);
  const [previewRows, setPreviewRows] = useState<CSVImportPreviewRow[]>([]);
  const [previewData, setPreviewData] = useState<CSVImportPreview | null>(null);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [importResult, setImportResult] = useState<CSVImportResult | null>(null);
  // Track which custom columns are "included" (default: not included)
  const [includedCustomColumns, setIncludedCustomColumns] = useState<Set<string>>(new Set());
  // Track which columns are explicitly ignored/removed
  const [ignoredColumns, setIgnoredColumns] = useState<Set<string>>(new Set());

  // Determine if this is a re-upload scenario
  const isReupload = existingParticipantCount > 0;

  // Build the header subtitle
  const headerSubtitle = partnerName
    ? `${cohortName} • ${partnerName}`
    : cohortName;

  // Reset state when modal closes
  const handleClose = () => {
    setActiveStep(0);
    setFile(null);
    setCsvHeaders([]);
    setCsvData([]);
    setMappings([]);
    setPreviewRows([]);
    setPreviewData(null);
    setImportResult(null);
    setIncludedCustomColumns(new Set());
    setIgnoredColumns(new Set());
    onClose();
  };

  // Parse CSV file
  const parseCSV = useCallback((text: string) => {
    const lines = text.split('\n').filter((line) => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV file must have at least a header row and one data row');
    }

    const parseRow = (row: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < row.length; i++) {
        const char = row[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };

    const headers = parseRow(lines[0]);
    const data = lines.slice(1).map(parseRow);

    return { headers, data };
  }, []);

  // Handle file drop
  const handleFileDrop = async (files: File[]) => {
    const selectedFile = files[0];
    if (!selectedFile) return;

    try {
      const text = await selectedFile.text();
      const { headers, data } = parseCSV(text);

      setFile(selectedFile);
      setCsvHeaders(headers);
      setCsvData(data);

      // Auto-map columns based on header names
      // Uses a two-pass approach: first try exact/alias matches, then fall back to substring
      // matching only for fields that weren't matched in the first pass.
      const autoMappings: CSVColumnMapping[] = headers.map((header) => {
        const headerLower = header.toLowerCase().replace(/[_\s-]/g, '');

        // Try to match standard fields — exact and alias matches only (no substring)
        for (const field of STANDARD_FIELDS) {
          const fieldLower = field.value.replace(/_/g, '');

          // Check exact match with field value
          if (headerLower === fieldLower) {
            return {
              csvColumn: header,
              targetField: field.value,
              isCustomField: false,
              customFieldName: null,
            };
          }

          // Check label (normalized)
          if (headerLower === field.label.toLowerCase().replace(/[_\s-]/g, '')) {
            return {
              csvColumn: header,
              targetField: field.value,
              isCustomField: false,
              customFieldName: null,
            };
          }

          // Check aliases
          if (field.aliases?.some(alias => headerLower === alias.toLowerCase().replace(/[_\s-]/g, ''))) {
            return {
              csvColumn: header,
              targetField: field.value,
              isCustomField: false,
              customFieldName: null,
            };
          }
        }

        // Default: unmapped (potential custom field, but not included by default)
        return {
          csvColumn: header,
          targetField: null,
          isCustomField: false,
          customFieldName: header,
        };
      });

      setMappings(autoMappings);
      setIncludedCustomColumns(new Set());
      setIgnoredColumns(new Set());

      // Automatically advance to Map Columns step
      setActiveStep(1);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to parse CSV file',
        color: 'red',
      });
    }
  };

  // Get sample values for a column (first 3 unique non-empty values)
  const getSampleValues = useCallback(
    (csvColumn: string): string => {
      const columnIndex = csvHeaders.indexOf(csvColumn);
      if (columnIndex === -1) return '';

      const uniqueValues = new Set<string>();
      for (const row of csvData) {
        const value = row[columnIndex]?.trim();
        if (value && uniqueValues.size < 3) {
          uniqueValues.add(value);
        }
        if (uniqueValues.size >= 3) break;
      }

      const values = Array.from(uniqueValues);
      if (values.length === 0) return '';
      return values.join(', ') + (csvData.length > 3 ? '...' : '');
    },
    [csvHeaders, csvData]
  );

  // Get value from raw CSV data for a specific row and column
  const getCsvValue = useCallback(
    (rowIndex: number, csvColumn: string): string | undefined => {
      const columnIndex = csvHeaders.indexOf(csvColumn);
      if (columnIndex === -1) return undefined;
      // rowIndex from previewRows is 1-based (row number in CSV after header)
      const dataRowIndex = rowIndex - 1;
      if (dataRowIndex < 0 || dataRowIndex >= csvData.length) return undefined;
      return csvData[dataRowIndex]?.[columnIndex]?.trim();
    },
    [csvHeaders, csvData]
  );

  // Update mapping for a CSV column
  const updateMapping = (csvColumn: string, targetField: string | null) => {
    setMappings((prev) =>
      prev.map((m) => {
        if (m.csvColumn === csvColumn) {
          const isStandard = targetField && STANDARD_FIELDS.some((f) => f.value === targetField);
          return {
            ...m,
            targetField,
            isCustomField: !isStandard && targetField !== null,
            customFieldName: !isStandard ? csvColumn : null,
          };
        }
        // Clear any other mapping to this target field
        if (targetField && m.targetField === targetField) {
          return {
            ...m,
            targetField: null,
            isCustomField: false,
            customFieldName: m.csvColumn,
          };
        }
        return m;
      })
    );
  };

  // Toggle custom column inclusion
  const toggleCustomInclusion = (csvColumn: string) => {
    setIncludedCustomColumns((prev) => {
      const next = new Set(prev);
      if (next.has(csvColumn)) {
        next.delete(csvColumn);
      } else {
        next.add(csvColumn);
      }
      return next;
    });
  };

  // Remove/ignore a column
  const ignoreColumn = (csvColumn: string) => {
    setIgnoredColumns((prev) => new Set([...prev, csvColumn]));
    setIncludedCustomColumns((prev) => {
      const next = new Set(prev);
      next.delete(csvColumn);
      return next;
    });
    // Clear any mapping
    setMappings((prev) =>
      prev.map((m) =>
        m.csvColumn === csvColumn
          ? { ...m, targetField: null, isCustomField: false }
          : m
      )
    );
  };

  // Get field type for a mapping
  const getFieldType = (mapping: CSVColumnMapping): FieldType => {
    if (mapping.targetField) {
      const field = STANDARD_FIELDS.find((f) => f.value === mapping.targetField);
      if (field) {
        return field.type;
      }
    }
    return 'custom';
  };

  // Build final mappings (used by both preview and import)
  const buildFinalMappings = useCallback(() => {
    return mappings.map((m) => {
      if (!m.targetField && includedCustomColumns.has(m.csvColumn) && !ignoredColumns.has(m.csvColumn)) {
        return {
          ...m,
          isCustomField: true,
          customFieldName: m.csvColumn,
        };
      }
      return m;
    });
  }, [mappings, includedCustomColumns, ignoredColumns]);

  // Calculate mapping summary
  const mappingSummary: MappingSummary = useMemo(() => {
    const visibleMappings = mappings.filter((m) => !ignoredColumns.has(m.csvColumn));

    // Standard fields = required + optional types
    const standardMapped = visibleMappings.filter((m) => {
      if (!m.targetField) return false;
      const field = STANDARD_FIELDS.find((f) => f.value === m.targetField);
      return field && (field.type === 'required' || field.type === 'optional');
    }).length;

    // Custom fields = custom type fields + user-included unmapped columns
    const customTypeMapped = visibleMappings.filter((m) => {
      if (!m.targetField) return false;
      const field = STANDARD_FIELDS.find((f) => f.value === m.targetField);
      return field && field.type === 'custom';
    }).length;

    const userIncludedCustom = Array.from(includedCustomColumns).filter(
      (col) => !ignoredColumns.has(col) && !mappings.find((m) => m.csvColumn === col)?.targetField
    ).length;

    const customIncluded = customTypeMapped + userIncludedCustom;

    return { standardMapped, customIncluded };
  }, [mappings, includedCustomColumns, ignoredColumns]);

  // Generate preview via API
  const generatePreview = useCallback(async () => {
    setPreviewing(true);
    try {
      const finalMappings = buildFinalMappings();

      const response = await participantsApi.previewImport(cohortId, {
        mappings: finalMappings,
        csvData,
      });

      setPreviewData(response);
      setPreviewRows(response.rows);
    } catch (error) {
      notifications.show({
        title: 'Preview Error',
        message: error instanceof Error ? error.message : 'Failed to generate preview',
        color: 'red',
      });
    } finally {
      setPreviewing(false);
    }
  }, [cohortId, csvData, buildFinalMappings]);

  // Handle import via API
  const handleImport = async () => {
    setImporting(true);

    try {
      const finalMappings = buildFinalMappings();

      const result = await participantsApi.executeImport(cohortId, {
        mappings: finalMappings,
        csvData,
        skipDuplicates: true,
      });

      setImportResult(result);
      // NOTE: Do NOT call onImportComplete() here - wait until user clicks "View Participants"
      // This ensures the confirmation view is displayed before the modal closes
    } catch (error) {
      notifications.show({
        title: 'Import Error',
        message: error instanceof Error ? error.message : 'Failed to import participants',
        color: 'red',
      });
    } finally {
      setImporting(false);
    }
  };

  // Handle closing after successful import (from confirmation view)
  const handleViewParticipants = () => {
    onImportComplete();
    handleClose();
  };

  // Check if we can proceed to next step
  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return file !== null;
      case 1: {
        // Must have required fields mapped (email always required)
        const mappedFields = mappings.filter((m) => m.targetField).map((m) => m.targetField);
        if (!mappedFields.includes('email')) return false;
        // If not a re-upload, require first_name and last_name for new participants
        if (!isReupload) {
          const requiredFields = ['first_name', 'last_name', 'email'];
          return requiredFields.every((f) => mappedFields.includes(f));
        }
        return true;
      }
      case 2:
        // Can proceed if there are any valid or update rows
        return previewRows.some((r) => r.status === 'valid' || r.status === 'update');
      default:
        return true;
    }
  };

  // Handle next step
  const handleNext = async () => {
    if (activeStep === 1) {
      await generatePreview();
    }
    setActiveStep((prev) => Math.min(prev + 1, 2) as ImportStep);
  };

  // Get optional field keys (non-required standard fields that are mapped)
  const optionalFieldKeys = useMemo(() => {
    return mappings
      .filter((m) => m.targetField && !REQUIRED_FIELDS.includes(m.targetField))
      .map((m) => m.targetField as string);
  }, [mappings]);

  // Count rows with empty optional fields (calculated locally)
  const emptyOptionalFieldsCount = useMemo(() => {
    if (previewRows.length === 0 || optionalFieldKeys.length === 0) return 0;

    return previewRows.filter((row) => {
      // Check if any optional field is empty for this row
      return optionalFieldKeys.some((fieldKey) => {
        const value = row.data[fieldKey as keyof typeof row.data];
        return !value || (typeof value === 'string' && value.trim() === '');
      });
    }).length;
  }, [previewRows, optionalFieldKeys]);

  // Calculate preview stats - use API response if available, but calculate warnings locally
  const previewStats: PreviewStats = useMemo(() => {
    const total = previewData ? previewData.totalRows : previewRows.length;
    const valid = previewData
      ? previewData.validRows + previewData.rowsToUpdate
      : previewRows.filter((r) => r.status === 'valid' || r.status === 'update').length;

    return {
      total,
      valid,
      warnings: emptyOptionalFieldsCount,
    };
  }, [previewData, previewRows, emptyOptionalFieldsCount]);

  // Get visible mappings (not ignored)
  const visibleMappings = mappings.filter((m) => !ignoredColumns.has(m.csvColumn));

  // Get columns for the review table
  const reviewColumns: ReviewColumn[] = useMemo(() => {
    const cols: ReviewColumn[] = [];

    // Add mapped standard fields
    for (const mapping of visibleMappings) {
      if (mapping.targetField) {
        const field = STANDARD_FIELDS.find((f) => f.value === mapping.targetField);
        if (field) {
          cols.push({
            key: mapping.targetField,
            label: field.label,
            required: field.type === 'required',
            isCustom: false,
          });
        }
      }
    }

    // Add included custom fields
    for (const csvColumn of includedCustomColumns) {
      if (!ignoredColumns.has(csvColumn)) {
        const mapping = mappings.find((m) => m.csvColumn === csvColumn);
        if (mapping && !mapping.targetField) {
          cols.push({
            key: csvColumn,
            label: csvColumn.toUpperCase().replace(/_/g, ' '),
            required: false,
            isCustom: true,
            csvColumn: csvColumn,
          });
        }
      }
    }

    return cols;
  }, [visibleMappings, includedCustomColumns, ignoredColumns, mappings]);

  return {
    // State
    file,
    csvHeaders,
    csvData,
    mappings,
    activeStep,
    previewRows,
    previewData,
    importing,
    previewing,
    importResult,
    includedCustomColumns,
    ignoredColumns,
    isReupload,
    visibleMappings,
    reviewColumns,
    headerSubtitle,

    // Computed values
    canProceed,
    mappingSummary,
    previewStats,
    emptyOptionalFieldsCount,

    // Handlers
    handleClose,
    handleFileDrop,
    handleNext,
    handleImport,
    handleViewParticipants,
    updateMapping,
    toggleCustomInclusion,
    ignoreColumn,
    getSampleValues,
    getCsvValue,
    getFieldType,
    setActiveStep,
    setFile,
    setCsvHeaders,
    setCsvData,
    setMappings,
  };
}
